import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface AmenityModalProps {
  /** 設備一覧 (日本語 or 英語), 例: ["WiFi","Air Conditioning"] */
  facility?: string[];
  facility_en?: string[];

  /** バスアメニティ一覧 (日本語 or 英語), 例: ["シャワー","タオル"] */
  bathAmenity?: string[];
  /** バスアメニティ一覧 (英語版), 例: ["Shower","Towels"] */
  bathAmenity_en?: string[];

  /** モーダルを開くトリガーのテキスト */
  linkText?: string;
}

export const AmenityModal: React.FC<AmenityModalProps> = ({
  facility,
  facility_en,
  bathAmenity,
  bathAmenity_en,
  linkText = 'Amenities / Appliances',
}) => {
  const location = useLocation();
  const isMonthlyPage = location.pathname.includes('monthly');
  // URLに"/en"が含まれていれば英語モード、それ以外は日本語モードとする
  const isEnglish = location.pathname.includes('/en');

  const [isOpen, setIsOpen] = useState(false);

  // モーダル開閉時にbodyのスクロールを制御
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleOpenModal = () => setIsOpen(true);
  const handleCloseModal = () => setIsOpen(false);

  const noFacilityMessage = '設備情報はありません';

  // 英語モードの場合はfacility_en、そうでなければfacilityを使用する
  const facilityList = isEnglish ? facility_en || facility : facility;
  const isFacilityEmpty = !facilityList || facilityList.length === 0;

  // 表示するBath Amenityは、英語モードならbathAmenity_en、そうでなければbathAmenity
  const bathAmenityList = isEnglish ? bathAmenity_en : bathAmenity;
  const isBathAmenityEmpty = !bathAmenityList || bathAmenityList.length === 0;

  // マンスリーページの場合はfacilityのみ確認し、そうでなければfacilityとbathAmenityの両方が空かチェック
  const isAllEmpty = isMonthlyPage
    ? isFacilityEmpty
    : isFacilityEmpty && isBathAmenityEmpty;

  return (
    <>
      {/* モーダルを開くリンク */}
      <div className="underline cursor-pointer mr-2" onClick={handleOpenModal}>
        {linkText}
      </div>

      {/* モーダル */}
      {isOpen && (
        <div
          className="
            fixed inset-0 z-50 
            flex items-center justify-center 
            bg-whiteYellow bg-opacity-90
          "
          onClick={handleCloseModal}
        >
          <div
            className="
              relative
              bg-whiteYellow
              rounded
              max-h-[80%]
              overflow-auto
              cursor-default
              p-4
            "
            onClick={(e) => e.stopPropagation()}
          >
            {/* コンテンツ枠 */}
            <div
              className="
                w-[300px]
                min-h-[200px]
                mx-auto
                p-2
                border border-black
              "
            >
              {isAllEmpty ? (
                <p className="text-center py-4">{noFacilityMessage}</p>
              ) : (
                <>
                  {/* 上段: facility */}
                  {!isFacilityEmpty && (
                    <>
                      <h3 className="text-start mb-2 font-bold">(Facility)</h3>
                      <ul className="list-disc ml-4">
                        {facilityList!.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {/* 下段: bathAmenityは、マンスリーページでは表示せず、言語に合わせたものを表示 */}
                  {!isMonthlyPage && !isBathAmenityEmpty && (
                    <>
                      <h3 className="text-start mb-2 mt-4 font-bold">
                        (Bath Amenity)
                      </h3>
                      <ul className="list-disc ml-4">
                        {bathAmenityList!.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}
            </div>

            {/* 閉じるボタン */}
            <div className="mt-4 text-center">
              <button onClick={handleCloseModal} className="text-xl font-bold">
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
